"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useSearchContext } from "fumadocs-ui/contexts/search";
import { useTheme } from "next-themes";
import { Globe, Monitor, Moon, Search, Sun } from "lucide-react";
import { Badge } from "@velpha/ui/components/badge";
import { Button } from "@velpha/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@velpha/ui/components/dropdown-menu";
import { Kbd, KbdGroup } from "@velpha/ui/components/kbd";
import { getTranslations, i18n, locales } from "@/lib/i18n";

const localePattern = new RegExp(`^/(${i18n.languages.join("|")})(?:/|$)`);

const navItems = [
  { key: "docs", path: "/docs" },
  { key: "releaseNotes", path: "/release-notes" },
] as const;

export function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const search = useSearchContext();
  const { theme, setTheme } = useTheme();

  const localeMatch = pathname.match(localePattern);
  const locale = localeMatch?.[1] ?? i18n.defaultLanguage;
  const localeBase = `/${locale}`;
  const localeDocsBase = `${localeBase}/docs`;
  const t = getTranslations(locale);

  function getLocaleSwitchPath(targetLocale: string) {
    if (pathname === `${localeBase}/release-notes` || pathname.startsWith(`${localeBase}/release-notes/`)) {
      return pathname.replace(localePattern, `/${targetLocale}/`);
    }

    return `/${targetLocale}/docs`;
  }

  return (
    <header className="sticky top-0 z-50 hidden w-full border-b bg-background/95 backdrop-blur-sm md:block">
      <div className="mx-auto flex h-14 max-w-(--fd-layout-width,1550px) items-center gap-6 px-3">
        <Link href={localeDocsBase} className="flex items-center gap-2">
          <Image src="/logo.png" alt="Velpha" width={24} height={24} className="size-6" />
          <span className="text-base font-semibold tracking-tight">Velpha</span>
          <Badge variant="outline" className="rounded-md px-1.5 text-[10px] font-medium">
            Docs
          </Badge>
        </Link>

        <nav className="flex items-center gap-2">
          {navItems.map((item) => {
            const href = `${localeBase}${item.path}`;
            const isActive = pathname === href || pathname.startsWith(`${href}/`);

            return (
              <Button
                key={item.path}
                asChild
                variant={isActive ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-md px-3 text-sm"
              >
                <Link href={href} className="flex items-center gap-2">
                  <span>{t.nav[item.key]}</span>
                </Link>
              </Button>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {search.enabled && (
            <Button type="button" onClick={() => search.setOpenSearch(true)} variant="outline" className="w-[220px]">
              <Search className="size-3.5 shrink-0" />
              <span className="flex-1 text-left">{t.search}</span>
              <KbdGroup>
                <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </KbdGroup>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label={t.chooseLanguage}>
                <Globe className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {locales.map((l) => (
                <DropdownMenuCheckboxItem
                  key={l.code}
                  checked={locale === l.code}
                  onClick={() => router.push(getLocaleSwitchPath(l.code))}
                >
                  {l.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label={t.chooseTheme}>
                <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem checked={theme === "light"} onClick={() => setTheme("light")}>
                <Sun className="size-4" />
                {t.light}
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={theme === "dark"} onClick={() => setTheme("dark")}>
                <Moon className="size-4" />
                {t.dark}
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={theme === "system"} onClick={() => setTheme("system")}>
                <Monitor className="size-4" />
                {t.system}
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
